import { useEffect, useState, useMemo } from 'react';
import { Accordion, AsyncDataHandler, Input, Select } from '@design-system';
import { IOption, SelectChangeVariant } from '@design-system/elements/Select/Select.types';
import { InputChangeVariant } from '@design-system/elements/Input/Input.types';
import { IEducationHistory } from 'src/shared/types/User';
import { useGetEducationHistories } from 'src/shared/queries/useGetEducationHistories';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import { useGetReferenceData } from 'src/shared/queries/useGetReferenceData';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useUserProfileStore } from 'src/stores/UserProfile';
import classNames from 'classnames';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';
import { processUpdatedDetails } from '../../UserProfile.service';

const INITIAL_EDUCATION_HISTORY: IEducationHistory = {
  countryCode: '',
  degreeReceived: '',
  fieldOfStudy: '',
  institution: '',
};

const Fields = ['countryCode', 'degreeReceived', 'fieldOfStudy', 'institution'];

const EducationInformation: React.FC = () => {
  const { isMobile } = useScreenSize();
  const { editMode, setDisplayUpdateButton, updatedFields, setUpdatedFields } = useUserProfileStore();
  const { data: userInfo } = useGetUser();
  const { data: eduHistoriesResponseData, isLoading, error } = useGetEducationHistories(userInfo?.beneId ?? 0);
  const { data: countries } = useGetCountries();
  const { data: degrees } = useGetReferenceData('DEGREE');
  const [activeIndexes, setActiveIndexes] = useState<number[]>([0]);
  const [educationHistories, setEducationHistories] = useState<IEducationHistory[]>([]);
  const beneId = userInfo?.beneId ?? 0;

  useEffect(() => {
    setDisplayUpdateButton(!!eduHistoriesResponseData);

    return () => setDisplayUpdateButton(true);
  }, [eduHistoriesResponseData]);

  useEffect(() => {
    if (eduHistoriesResponseData) {
      setEducationHistories(
        eduHistoriesResponseData?.length > 0 ? JSON.parse(JSON.stringify(eduHistoriesResponseData)) : []
      );
    }
  }, [eduHistoriesResponseData]);

  useEffect(() => {
    if (editMode && eduHistoriesResponseData != undefined) {
      const updatedEduHistories = processUpdatedDetails(
        educationHistories,
        eduHistoriesResponseData.map((address) => ({ ...address })),
        { beneId },
        'eduHistoryId',
        Fields
      );
      const newFields = { ...updatedFields };
      const hasUpdatedEduHistories = Object.values(updatedEduHistories).some((list) => list?.length > 0);
      if (hasUpdatedEduHistories) {
        newFields.educationHistory = updatedEduHistories;
      } else {
        delete newFields.educationHistory;
      }
      setUpdatedFields(Object.keys(newFields)?.length > 0 ? newFields : null);
    }
  }, [editMode, educationHistories, eduHistoriesResponseData]);

  const handleEducationHistoryFieldChange = (value: string, index: number, fieldName: string) => {
    setEducationHistories((prevHistories) =>
      prevHistories.map((history, prevHistoryIndex) =>
        prevHistoryIndex === index ? { ...history, [fieldName]: value } : history
      )
    );
  };

  const handleSelectFieldChange = (index: number, fieldName: string, option: IOption | null) => {
    setEducationHistories((prevHistories) =>
      prevHistories.map((history, prevHistoryIndex) =>
        prevHistoryIndex === index ? { ...history, [fieldName]: option?.value || '' } : history
      )
    );
  };

  const handleDeleteEducationHistory = async (index: number) => {
    setEducationHistories((prevHistories) => {
      const updatedEducationHistories = prevHistories.filter((_, prevHistoryIndex) => prevHistoryIndex !== index);
      if (updatedEducationHistories.length === 1) {
        setActiveIndexes([0]);
      }
      return updatedEducationHistories;
    });
  };

  const handleAddAnotherEducationHistory = () => {
    setEducationHistories((prevState) => [...prevState, { ...INITIAL_EDUCATION_HISTORY }]);
    setActiveIndexes([educationHistories.length]);
  };

  const countryOptions = useMemo(
    () => countries?.map((country) => ({ value: country.countryCode, label: country.countryName })) || [],
    [countries]
  );

  const degreeOptions = useMemo(
    () =>
      degrees?.map((degree: { code: string; description: string }) => ({
        value: degree.code,
        label: degree.description,
      })) || [],
    [degrees]
  );

  const styles = {
    row: 'flex flex-col lg:flex-row gap-[24px] lg:gap-[0px]',
    col4: 'w-full lg:w-1/3 lg:pr-[24px]',
  };

  const educationHistoryTabs =
    educationHistories.map(
      ({ eduHistoryId, degreeReceived = '', fieldOfStudy, institution, countryCode = '' }, index) => {
        const eduHistory = eduHistoriesResponseData?.find((history) => history.eduHistoryId === eduHistoryId);
        return {
          body: (
            <div className={styles.row}>
              <div className={styles.col4}>
                <Select
                  searchable
                  showClear={degreeReceived ? true : false}
                  label="Degree Received"
                  options={degreeOptions}
                  selectedOption={{ value: degreeReceived, label: degreeReceived }}
                  onChange={(option) => handleSelectFieldChange(index, 'degreeReceived', option)}
                  hasChanged={
                    !!eduHistory &&
                    degreeOptions?.length > 0 &&
                    (degreeReceived || '') !== (eduHistory.degreeReceived || '')
                  }
                  disabled={!editMode}
                  selectChangedStyle={SelectChangeVariant.Secondary}
                />
              </div>
              <div className={styles.col4}>
                <Input
                  label="Field of Study"
                  value={fieldOfStudy || ''}
                  onChange={(event) => handleEducationHistoryFieldChange(event.target.value, index, 'fieldOfStudy')}
                  hasChanged={!!eduHistory && (fieldOfStudy || '') !== (eduHistory.fieldOfStudy || '')}
                  disabled={!editMode}
                  inputChangedStyle={InputChangeVariant.Secondary}
                />
              </div>
              <div className={styles.col4}>
                <Input
                  label="Institution Name"
                  value={institution || ''}
                  onChange={(event) => handleEducationHistoryFieldChange(event.target.value, index, 'institution')}
                  hasChanged={!!eduHistory && (institution || '') !== (eduHistory.institution || '')}
                  disabled={!editMode}
                  inputChangedStyle={InputChangeVariant.Secondary}
                />
              </div>
              <div className={styles.col4}>
                <Select
                  label="Country / Location"
                  options={countryOptions}
                  selectedOption={{ value: countryCode, label: countryCode }}
                  onChange={(option) => handleSelectFieldChange(index, 'countryCode', option)}
                  searchable
                  showClear={countryCode ? true : false}
                  hasChanged={
                    !!eduHistory && countryOptions?.length > 0 && (countryCode || '') !== (eduHistory.countryCode || '')
                  }
                  disabled={!editMode}
                  selectChangedStyle={SelectChangeVariant.Secondary}
                />
              </div>
            </div>
          ),
        };
      }
    ) || [];

  return (
    <AsyncDataHandler isLoading={isLoading} error={error}>
      <div
        className={classNames({
          'mt-[60px]': !isLoading && !editMode,
          'pt-[20px]': !isLoading && editMode && isMobile,
        })}
      >
        <Accordion
          multiple
          allowToDelete={true}
          isDeleteDisabled={!editMode}
          activeIndex={activeIndexes}
          shouldPreserveFirstTab
          tabs={educationHistoryTabs}
          onTabChange={({ index }) => {
            setActiveIndexes(index as number[]);
          }}
          onDeleteTab={handleDeleteEducationHistory}
          tabHeaderPrefix="EDUCATION HISTORY"
          additionalTabConfig={{
            buttonLabel: 'EDUCATION HISTORY',
            onAdd: handleAddAnotherEducationHistory,
            disableAdd: !editMode,
          }}
        />
      </div>
    </AsyncDataHandler>
  );
};

export default EducationInformation;
